import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { publicRequest } from "../api/requestMethods";
import { requestDeliveryQuote } from "../api/deliveryQuote";
import { deliveryOfferOf } from "../utils/deliveryOffers";

const initialState = {
  deliveryFee: 0,
  distanceKm: 0,
  distanceSource: null,
  estimatedMinutes: 0,
  feeBand: null,
  zone: null,
  breakdown: null,
  vendorName: null,
  // Signed quote token from POST /api/quote. Forwarded to the server on
  // order-create as `clientQuoteToken`; the server re-verifies it and, if still
  // valid, honours the exact fee it was shown. NEVER authoritative on the client.
  quoteToken: null,
  quoteExpiresAt: null,
  fulfillment: null,
  freightTermsAccepted: false,
  // The server refused an order because the free-delivery offer this quote
  // showed is no longer the shopper's (409 DELIVERY_OFFER_UNAVAILABLE): used
  // by another order, or switched off. useDeliveryFee re-quotes when it sees
  // this, so the checkout shows the real fee before the next tap.
  offerWithdrawn: false,
  // The free-delivery offer the server applied to this quote, if any.
  offer: null,
  // The server's goods subtotal, goods-plus-delivery total and minimum-order
  // verdict for the quoted lines (POST /api/quote, 2 Nov 2026), so a screen
  // shows the server's numbers instead of adding the fee to a sum of its own.
  itemsSubtotal: null,
  total: null,
  minimumOrder: null,
  // The payment processing fee the server adds for the checkout's payment
  // method ({ fee, label, rail, show }), already inside `total`.
  paymentProcessing: null,
  status: "idle",
  error: null,
};

export const DELIVERY_OFFER_UNAVAILABLE = "DELIVERY_OFFER_UNAVAILABLE";

/* Any vertical's rejected order-create whose server code says the offer went. */
const isOfferWithdrawnRejection = (action) =>
  typeof action?.type === "string" &&
  action.type.endsWith("/rejected") &&
  action.payload?.code === DELIVERY_OFFER_UNAVAILABLE;

export const fetchDeliveryFee = createAsyncThunk(
  "deliveryFee/fetchDeliveryFee",
  async ({ vendorId, customerLat, customerLng }) => {
    const response = await publicRequest.get(
      `/api/delivery-fee?vendorId=${vendorId}&customerLat=${customerLat}&customerLng=${customerLng}`
    );
    return response.data;
  }
);

// POST /api/quote: server-signed quote { fee, distanceKm, zone, breakdown,
// quoteToken, expiresAt }. The signed sibling of /api/delivery-fee and the
// single source the client uses to DISPLAY the fee and later prove (via
// quoteToken) which number it showed. The request itself lives in
// api/deliveryQuote so the carts' estimate sends exactly the same thing.
export const fetchDeliveryQuote = createAsyncThunk(
  "deliveryFee/fetchDeliveryQuote",
  async (params, { rejectWithValue }) => {
    try {
      return await requestDeliveryQuote(params);
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || { message: error.message || "Failed to fetch quote" }
      );
    }
  }
);

const deliveryFeeSlice = createSlice({
  name: "deliveryFee",
  initialState,
  reducers: {
    resetDeliveryFee: () => {
      return initialState;
    },
    setFreightTermsAccepted: (state, action) => {
      state.freightTermsAccepted = action.payload === true;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchDeliveryFee.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchDeliveryFee.fulfilled, (state, action) => {
        state.deliveryFee = action.payload.deliveryFee || 0;
        state.distanceKm = action.payload.distanceKm || 0;
        state.distanceSource = action.payload.distanceSource || null;
        state.estimatedMinutes = action.payload.estimatedMinutes || 0;
        state.feeBand = action.payload.feeBand || null;
        state.zone = action.payload.zone || null;
        state.breakdown = action.payload.breakdown || null;
        state.vendorName = action.payload.vendorName || null;
        state.status = "succeeded";
      })
      .addCase(fetchDeliveryFee.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Quote endpoint — populates the same display fields PLUS the signed token.
      .addCase(fetchDeliveryQuote.pending, (state) => {
        state.status = "loading";
        state.freightTermsAccepted = false;
      })
      .addCase(fetchDeliveryQuote.fulfilled, (state, action) => {
        state.deliveryFee = action.payload.fee || 0;
        state.distanceKm = action.payload.distanceKm || 0;
        state.distanceSource = action.payload.distanceSource || null;
        state.estimatedMinutes = action.payload.estimatedDeliveryMinutes || 0;
        state.zone = action.payload.zone || null;
        state.breakdown = action.payload.breakdown || null;
        state.quoteToken = action.payload.quoteToken || null;
        state.quoteExpiresAt = action.payload.expiresAt || null;
        state.fulfillment = action.payload.fulfillment || null;
        state.offer = deliveryOfferOf(action.payload);
        state.itemsSubtotal = action.payload.itemsSubtotal ?? null;
        state.total = action.payload.total ?? null;
        state.minimumOrder = action.payload.minimumOrder || null;
        state.paymentProcessing = action.payload.paymentProcessing || null;
        state.offerWithdrawn = false;
        state.status = "succeeded";
      })
      .addCase(fetchDeliveryQuote.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload?.message || action.error?.message || "Failed to fetch quote";
      })
      .addMatcher(isOfferWithdrawnRejection, (state) => {
        state.offerWithdrawn = true;
      });
  },
});

export default deliveryFeeSlice.reducer;

export const { resetDeliveryFee, setFreightTermsAccepted } = deliveryFeeSlice.actions;
